import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Clock, Receipt, ArrowRight } from 'lucide-react';
import { dashboardService } from '../../services/dashboardService';

const UnbilledWorkWidget = ({ viewType }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dashboardService.getUnbilledWork(viewType);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching unbilled work:', error);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [viewType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow border p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4 w-40"></div>
          <div className="space-y-3">
            <div className="h-16 bg-gray-100 rounded"></div>
            <div className="h-16 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow border">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <FileText className="h-5 w-5 text-amber-500 mr-2" />
            Unbilled Work
          </h3>
          <button
            onClick={() => navigate('/time-tracking')}
            className="text-sm text-yellow-600 hover:text-yellow-800 font-medium flex items-center"
          >
            Time Entries <ArrowRight className="h-3 w-3 ml-1" />
          </button>
        </div>
      </div>

      <div className="p-6">
        {!data ? (
          <div className="text-center py-6">
            <div className="text-gray-400 mb-3">
              <FileText className="h-12 w-12 mx-auto" />
            </div>
            <p className="text-gray-600">Unable to load unbilled work data</p>
          </div>
        ) : data.total_unbilled === 0 ? (
          <div className="text-center py-6">
            <div className="text-gray-400 mb-3">
              <FileText className="h-12 w-12 mx-auto" />
            </div>
            <p className="text-gray-600 font-medium">No unbilled work</p>
            <p className="text-sm text-gray-500 mt-1">Everything has been invoiced</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Total Unbilled */}
            <div className="text-center p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="text-xs font-medium text-amber-600 uppercase tracking-wide">Total Unbilled</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(data.total_unbilled)}</div>
            </div>

            {/* Breakdown */}
            <div className="space-y-3">
              {/* Time Entries */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <Clock className="h-4 w-4 text-blue-500 mr-2" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Time Entries</div>
                    <div className="text-xs text-gray-500">
                      {data.time_entries.count} entries &middot; {data.time_entries.hours.toFixed(1)} hours
                    </div>
                  </div>
                </div>
                <span className="text-sm font-semibold text-gray-900">
                  {formatCurrency(data.time_entries.amount)}
                </span>
              </div>

              {/* Expenses */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <Receipt className="h-4 w-4 text-purple-500 mr-2" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Expenses</div>
                    <div className="text-xs text-gray-500">
                      {data.expenses.count} items
                    </div>
                  </div>
                </div>
                <span className="text-sm font-semibold text-gray-900">
                  {formatCurrency(data.expenses.amount)}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnbilledWorkWidget;
